import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  createPasskeyVault,
  createPassphraseVault,
  defaultSettings,
  destroyVault,
  markBackedUp,
  readSettings,
  readVault,
  unlockVault,
  unlockWithBiometrics,
  enableBiometricUnlock,
  disableBiometricUnlock,
  assertBiometricApproval,
  biometricUnlockReady,
  passkeysAvailable,
  writeSettings,
  readSession,
  recordUnlock,
  destroySession,
  rotatePassphrase,
  type SessionInfo,
  type VaultRecord,
  type VaultSettings,
} from "./vault";
import {
  newContactId,
  readContacts,
  sanitizeLabel,
  sanitizeNote,
  sanitizeTag,
  writeContacts,
  type Contact,
} from "./addressBook";
import { accountFromMnemonic } from "./mnemonic";
import { generateAccount, signAndSubmit, xrpToDrops, type SubmitResult } from "./xrpl";
import type { ServiceFee } from "./fees";
import { reportTransactionFailure } from "@/lib/telemetry";

interface WalletContextValue {
  hydrated: boolean;
  vault: VaultRecord | null;
  address: string | null;
  unlocked: boolean;
  settings: VaultSettings;
  session: SessionInfo | null;
  contacts: Contact[];
  /** Secrets shown exactly once, right after creation, for backup. Cleared after. */
  pendingBackup: {
    seed: string;
    address: string;
    mnemonic?: string;
    usedExtraWord?: boolean;
  } | null;
  createWithPasskey: () => Promise<void>;
  createWithPassphrase: (passphrase: string) => Promise<void>;
  /** Creates a vault from a locally generated BIP39 phrase (+ optional 25th word). */
  createFromMnemonic: (input: {
    mnemonic: string;
    extraWord?: string;
    protection: "passkey" | "passphrase";
    passphrase?: string;
  }) => Promise<void>;
  importMnemonic: (input: {
    mnemonic: string;
    extraWord?: string;
    protection: "passkey" | "passphrase";
    passphrase?: string;
  }) => Promise<void>;
  changePassphrase: (current: string | undefined, next: string) => Promise<void>;
  /** true when this device can open the vault with biometrics */
  biometricsEnabled: boolean;
  /** true when the browser exposes a passkey/biometric authenticator at all */
  biometricsSupported: boolean;
  /** Enrols device biometrics for unlocking. Requires an unlocked vault. */
  enableBiometrics: () => Promise<void>;
  disableBiometrics: () => void;
  /** Unlocks using device biometrics (Face ID / Touch ID / Windows Hello). */
  unlockBiometric: () => Promise<void>;
  saveContact: (input: {
    id?: string;
    label: string;
    address: string;
    tag?: string;
    note?: string;
  }) => Contact;
  removeContact: (id: string) => void;
  importSeed: (
    seed: string,
    mode: "passkey" | "passphrase",
    passphrase?: string,
  ) => Promise<void>;
  confirmBackup: () => void;
  unlock: (passphrase?: string) => Promise<void>;
  lock: () => void;
  wipe: () => void;
  updateSettings: (patch: Partial<VaultSettings>) => void;
  /**
   * Signs and submits the user's transaction. When `fee` is provided and
   * chargeable, a separate service-fee Payment is submitted ONLY after the
   * user's transaction validates successfully.
   */
  submit: (
    tx: Record<string, unknown>,
    options?: { fee?: ServiceFee | null },
  ) => Promise<SubmitOutcome>;
}

export interface SubmitOutcome extends SubmitResult {
  serviceFee?: {
    xrp: number;
    charged: boolean;
    hash?: string;
    error?: string;
  };
}

// Kept on globalThis so a hot-reloaded copy of this module reuses the same
// context instance as the already-mounted provider (avoids "must be used inside
// WalletProvider" after an HMR update).
const globalStore = globalThis as unknown as {
  __vaultkeyWalletContext?: React.Context<WalletContextValue | null>;
};
const WalletContext =
  globalStore.__vaultkeyWalletContext ??
  (globalStore.__vaultkeyWalletContext = createContext<WalletContextValue | null>(null));

export function WalletProvider({ children }: { children: ReactNode }) {
  const [hydrated, setHydrated] = useState(false);
  const [vault, setVault] = useState<VaultRecord | null>(null);
  const [settings, setSettings] = useState<VaultSettings>(defaultSettings);
  const settingsRef = useRef<VaultSettings>(defaultSettings);

  const [unlocked, setUnlocked] = useState(false);
  const [pendingBackup, setPendingBackup] = useState<
    { seed: string; address: string; mnemonic?: string; usedExtraWord?: boolean } | null
  >(null);
  const [session, setSession] = useState<SessionInfo | null>(null);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const seedRef = useRef<string | null>(null);
  const lastActivity = useRef<number>(Date.now());

  useEffect(() => {
    setVault(readVault());
    const stored = readSettings();
    settingsRef.current = stored;
    setSettings(stored);
    setSession(readSession());
    setContacts(readContacts());
    setHydrated(true);
  }, []);

  const lock = useCallback(() => {
    seedRef.current = null;
    setUnlocked(false);
    setPendingBackup(null);
  }, []);

  // Idle auto-lock.
  useEffect(() => {
    if (!unlocked) return;
    const bump = () => {
      lastActivity.current = Date.now();
    };
    const events: (keyof WindowEventMap)[] = ["pointerdown", "keydown", "focus"];
    events.forEach((e) => window.addEventListener(e, bump));
    const interval = window.setInterval(() => {
      if (Date.now() - lastActivity.current > settings.autoLockSeconds * 1000) lock();
    }, 5000);
    return () => {
      events.forEach((e) => window.removeEventListener(e, bump));
      window.clearInterval(interval);
    };
  }, [unlocked, settings.autoLockSeconds, lock]);

  const adopt = useCallback((record: VaultRecord, seed: string) => {
    seedRef.current = seed;
    setVault(record);
    setUnlocked(true);
    setPendingBackup({ seed, address: record.address });
  }, []);

  const createFromMnemonic = useCallback(
    async ({
      mnemonic,
      extraWord,
      protection,
      passphrase,
    }: {
      mnemonic: string;
      extraWord?: string;
      protection: "passkey" | "passphrase";
      passphrase?: string;
    }) => {
      const { seed, address } = await accountFromMnemonic(mnemonic, extraWord);
      const record =
        protection === "passkey"
          ? await createPasskeyVault(seed, address)
          : await createPassphraseVault(seed, address, passphrase ?? "");
      seedRef.current = seed;
      setVault(record);
      setUnlocked(true);
      setSession(recordUnlock());
      setPendingBackup({
        seed,
        address,
        mnemonic,
        usedExtraWord: Boolean(extraWord?.trim()),
      });
    },
    [],
  );

  const importMnemonic = useCallback(
    async ({
      mnemonic,
      extraWord,
      protection,
      passphrase,
    }: {
      mnemonic: string;
      extraWord?: string;
      protection: "passkey" | "passphrase";
      passphrase?: string;
    }) => {
      const { seed, address } = await accountFromMnemonic(mnemonic, extraWord);
      const record =
        protection === "passkey"
          ? await createPasskeyVault(seed, address)
          : await createPassphraseVault(seed, address, passphrase ?? "");
      seedRef.current = seed;
      setVault({ ...record, backedUp: true });
      markBackedUp();
      setUnlocked(true);
      setSession(recordUnlock());
    },
    [],
  );

  const enableBiometrics = useCallback(async () => {
    const seed = seedRef.current;
    if (!seed) throw new Error("Unlock the wallet first.");
    const record = await enableBiometricUnlock(seed);
    setVault(record);
  }, []);

  const disableBiometrics = useCallback(() => {
    setVault(disableBiometricUnlock());
  }, []);

  const unlockBiometric = useCallback(async () => {
    const seed = await unlockWithBiometrics();
    seedRef.current = seed;
    lastActivity.current = Date.now();
    setUnlocked(true);
    setSession(recordUnlock());
  }, []);

  const changePassphrase = useCallback(async (current: string | undefined, next: string) => {
    const record = await rotatePassphrase(current, next);
    setVault(record);
  }, []);

  const saveContact = useCallback(
    ({
      id,
      label,
      address,
      tag,
      note,
    }: { id?: string; label: string; address: string; tag?: string; note?: string }) => {
      const sanitizedTag = sanitizeTag(tag ?? "");
      const sanitizedNote = sanitizeNote(note ?? "");
      const entry: Contact = {
        id: id ?? newContactId(),
        label: sanitizeLabel(label),
        address: address.trim(),
        createdAt: Date.now(),
        ...(sanitizedTag ? { tag: sanitizedTag } : {}),
        ...(sanitizedNote ? { note: sanitizedNote } : {}),
      };
      setContacts((prev) => {
        const next = id ? prev.map((c) => (c.id === id ? { ...entry, createdAt: c.createdAt } : c)) : [entry, ...prev];
        writeContacts(next);
        return next;
      });
      return entry;
    },
    [],
  );

  const removeContact = useCallback((id: string) => {
    setContacts((prev) => {
      const next = prev.filter((c) => c.id !== id);
      writeContacts(next);
      return next;
    });
  }, []);

  const createWithPasskey = useCallback(async () => {
    const { seed, address } = await generateAccount();
    const record = await createPasskeyVault(seed, address);
    adopt(record, seed);
  }, [adopt]);

  const createWithPassphrase = useCallback(
    async (passphrase: string) => {
      const { seed, address } = await generateAccount();
      const record = await createPassphraseVault(seed, address, passphrase);
      adopt(record, seed);
    },
    [adopt],
  );

  const importSeed = useCallback(
    async (seed: string, mode: "passkey" | "passphrase", passphrase?: string) => {
      const { walletFromSeed } = await import("./xrpl");
      const wallet = await walletFromSeed(seed.trim());
      const record =
        mode === "passkey"
          ? await createPasskeyVault(seed.trim(), wallet.address)
          : await createPassphraseVault(seed.trim(), wallet.address, passphrase ?? "");
      seedRef.current = seed.trim();
      setVault({ ...record, backedUp: true });
      markBackedUp();
      setUnlocked(true);
    },
    [],
  );

  const confirmBackup = useCallback(() => {
    markBackedUp();
    setVault((v) => (v ? { ...v, backedUp: true } : v));
    setPendingBackup(null);
  }, []);

  const unlock = useCallback(async (passphrase?: string) => {
    const seed = await unlockVault(passphrase);
    seedRef.current = seed;
    lastActivity.current = Date.now();
    setUnlocked(true);
    setSession(recordUnlock());
  }, []);

  const wipe = useCallback(() => {
    destroyVault();
    destroySession();
    seedRef.current = null;
    setSession(readSession());
    setVault(null);
    setUnlocked(false);
    setPendingBackup(null);
  }, []);

  const updateSettings = useCallback((patch: Partial<VaultSettings>) => {
    setSettings((prev) => {
      const next = { ...prev, ...patch };
      settingsRef.current = next;
      writeSettings(next);
      return next;
    });
  }, []);

  const submit = useCallback(
    async (
      tx: Record<string, unknown>,
      options?: { fee?: ServiceFee | null },
    ): Promise<SubmitOutcome> => {
      const seed = seedRef.current;
      if (!seed) throw new Error("Wallet is locked.");
      lastActivity.current = Date.now();

      // Biometric signing: a fresh Face ID / Touch ID check is required before
      // the seed is used, and before anything reaches the ledger.
      if (settingsRef.current.biometricSigning) await assertBiometricApproval();

      const primary = await signAndSubmit(seed, tx);

      // Failed ledger submissions are reported so real-user failures are visible
      // in the app's own server log. No seed or key material is included.
      if (!primary.succeeded) {
        reportTransactionFailure({
          message: `XRPL submission did not validate: ${primary.result ?? "unknown result"}`,
          txType: String(tx["TransactionType"] ?? "unknown"),
          ...(primary.result ? { resultCode: String(primary.result) } : {}),
          ...(primary.hash ? { txHash: String(primary.hash) } : {}),
          ...(vault?.address ? { address: vault.address } : {}),
        });
      }

      const fee = options?.fee ?? null;
      const chargeable =
        Boolean(fee) && fee!.xrp > 0 && Boolean(fee!.destination) && primary.succeeded;

      // The service fee is a second, independent Payment. It is only ever
      // submitted after the user's own transaction has validated on-ledger.
      if (!chargeable) return primary;

      try {
        const feeTx = await signAndSubmit(seed, {
          TransactionType: "Payment",
          Account: vault?.address ?? String(tx["Account"] ?? ""),
          Destination: fee!.destination,
          Amount: xrpToDrops(fee!.xrp),
        });
        return {
          ...primary,
          serviceFee: {
            xrp: fee!.xrp,
            charged: feeTx.succeeded,
            hash: feeTx.hash,
            ...(feeTx.succeeded ? {} : { error: feeTx.result }),
          },
        };
      } catch (err) {
        // A failed fee collection must never invalidate the user's transaction.
        return {
          ...primary,
          serviceFee: {
            xrp: fee!.xrp,
            charged: false,
            error: err instanceof Error ? err.message : "Fee submission failed.",
          },
        };
      }
    },
    [vault?.address],
  );

  const value = useMemo<WalletContextValue>(
    () => ({
      hydrated,
      vault,
      address: vault?.address ?? null,
      unlocked,
      settings,
      session,
      contacts,
      pendingBackup,
      createWithPasskey,
      createWithPassphrase,
      createFromMnemonic,
      importMnemonic,
      changePassphrase,
      biometricsEnabled: biometricUnlockReady(vault),
      biometricsSupported: passkeysAvailable(),
      enableBiometrics,
      disableBiometrics,
      unlockBiometric,
      saveContact,
      removeContact,
      importSeed,
      confirmBackup,
      unlock,
      lock,
      wipe,
      updateSettings,
      submit,
    }),
    [
      hydrated,
      vault,
      unlocked,
      settings,
      session,
      contacts,
      pendingBackup,
      createWithPasskey,
      createWithPassphrase,
      createFromMnemonic,
      importMnemonic,
      changePassphrase,
      enableBiometrics,
      disableBiometrics,
      unlockBiometric,
      saveContact,
      removeContact,
      importSeed,
      confirmBackup,
      unlock,
      lock,
      wipe,
      updateSettings,
      submit,
    ],
  );

  return <WalletContext.Provider value={value}>{children}</WalletContext.Provider>;
}

export function useWallet(): WalletContextValue {
  const ctx = useContext(WalletContext);
  if (!ctx) throw new Error("useWallet must be used inside WalletProvider");
  return ctx;
}
